import React, { useRef, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, InputGroup, FormControl, Button } from "components/bootstrap";
import NumberUtils from "util/NumberUtils";
import "./UploadField.css";
const isFileSizeValid = (file, maxSize) => maxSize ? file.size <= maxSize : true;
const UploadField = ({
  accept = void 0,
  uploadSuccessful = void 0,
  uploadConfig = {
    maxUploadSize: 0
  },
  enableRemoval,
  currentFile = void 0,
  isLoading,
  isUploading,
  onUploadFile,
  onDeleteFile
}) => {
  const { t } = useTranslation("enterprise_lookup");
  const uploadRef = useRef();
  const [hasError, setHasError] = useState(false);
  const setUploadRef = useCallback((ref) => {
    uploadRef.current = ref;
  }, []);
  const onOpenBrowse = useCallback(() => {
    if (!uploadRef.current || isUploading || isLoading) {
      return;
    }
    uploadRef.current.click();
  }, [uploadRef, isUploading, isLoading]);
  const onChangeFile = useCallback(
    (e) => {
      if (uploadConfig && isFileSizeValid(e.target.files[0], uploadConfig == null ? void 0 : uploadConfig.maxUploadSize)) {
        setHasError(false);
        onUploadFile(e.target.files);
      } else {
        setHasError(true);
      }
    },
    [onUploadFile, uploadConfig]
  );
  const inputValue = useMemo(() => {
    if (currentFile) {
      return currentFile.name;
    }
    if (isLoading) {
      return t("upload_field.loading_file");
    }
    if (isUploading) {
      return t("upload_field.uploading_file");
    }
    return "";
  }, [currentFile, isUploading, isLoading, t]);
  const helpText = useMemo(() => {
    if (uploadSuccessful === true) {
      return t("upload_field.upload_success");
    }
    if (uploadSuccessful === false) {
      return t("upload_field.upload_failed");
    }
    if (uploadConfig == null ? void 0 : uploadConfig.maxUploadSize) {
      return t("upload_field.help_max_file_size", {
        maxFileSize: NumberUtils.formatBytes(uploadConfig == null ? void 0 : uploadConfig.maxUploadSize)
      });
    }
    return t("upload_field.help_default");
  }, [uploadSuccessful, uploadConfig, t]);
  const errorHelpText = useMemo(
    () => t("upload_field.error_file_too_large", {
      maxFileSize: NumberUtils.formatBytes(uploadConfig == null ? void 0 : uploadConfig.maxUploadSize)
    }),
    [uploadConfig, t]
  );
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "path",
      name: "path",
      label: t("upload_field.csv_file"),
      help: hasError ? errorHelpText : helpText,
      labelClassName: "col-sm-3",
      bsStyle: hasError ? "error" : null,
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(InputGroup, null, /* @__PURE__ */ React.createElement(InputGroup.Button, null, /* @__PURE__ */ React.createElement(Button, { disabled: isUploading || isLoading, onClick: onOpenBrowse }, t("upload_field.browse"))), /* @__PURE__ */ React.createElement(
      FormControl,
      {
        onClick: onOpenBrowse,
        type: "text",
        value: inputValue,
        onChange: () => {
        },
        tabIndex: -1,
        placeholder: t("upload_field.no_file_selected")
      }
    ), currentFile && enableRemoval && /* @__PURE__ */ React.createElement(InputGroup.Button, null, /* @__PURE__ */ React.createElement(Button, { bsStyle: "danger", onClick: onDeleteFile }, t("upload_field.remove"))), /* @__PURE__ */ React.createElement(
      "input",
      {
        className: "UploadField__uploadInput",
        type: "file",
        accept,
        "aria-label": t("upload_field.csv_file"),
        onChange: onChangeFile,
        ref: setUploadRef
      }
    ))
  ));
};
export default UploadField;
